import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Gift, Copy, Rocket, Zap, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CODE = "LAUNCH50";

export default function Launch() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    api.get("/launch/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, [user]);

  const claim = async () => {
    if (!user) { nav("/register?ref=launch"); return; }
    setClaiming(true);
    try {
      const { data } = await api.post("/launch/claim", { code: CODE });
      toast.success(`+${data.credits_added} credits added — welcome aboard`);
      await refresh();
      const { data: s } = await api.get("/launch/status");
      setStatus(s);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setClaiming(false); }
  };

  const copyCode = () => { navigator.clipboard.writeText(CODE); toast.success("Code copied"); };

  const alreadyClaimed = user?.launch_promo_claimed;
  const remaining = status?.remaining ?? 100;
  const soldOut = remaining <= 0;

  return (
    <div className="relative min-h-[80vh] overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-70 pointer-events-none" />
      <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[900px] h-[420px] bg-cyan-500/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-6">
            <Rocket className="w-3.5 h-3.5 text-cyan-signal" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-signal">Launch week · limited</span>
          </div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-display font-extrabold tracking-tighter leading-[0.95]">
            Double your credits.
            <span className="block text-cyan-signal mt-2">First 100 signups only.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-xl mx-auto">
            Redeem code <button onClick={copyCode} className="font-mono text-cyan-signal hover:underline" data-testid="launch-code-copy">{CODE}</button> at signup and we'll drop 500 bonus credits into your account — on top of the 500 free-tier credits you already get.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-10">
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid="launch-stat-remaining">
            <div className="text-4xl font-display font-bold text-cyan-signal tabular-nums">{remaining}</div>
            <div className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-500">spots left</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="text-4xl font-display font-bold text-emerald-400 tabular-nums">+500</div>
            <div className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-500">bonus credits</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="text-4xl font-display font-bold text-amber-400 tabular-nums">$0</div>
            <div className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-500">no card needed</div>
          </div>
        </div>

        <div className="max-w-md mx-auto rounded-2xl border border-cyan-500/40 bg-gradient-to-br from-slate-900 to-cyan-950/30 p-6 sm:p-8 text-center shadow-[0_0_60px_-12px_rgba(0,240,255,0.35)]">
          <Gift className="w-10 h-10 text-cyan-signal mx-auto mb-4" />
          <div className="inline-flex items-center gap-2 mx-auto px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-800 mb-5">
            <code className="font-mono text-lg text-cyan-signal tracking-widest">{CODE}</code>
            <button onClick={copyCode} className="text-slate-500 hover:text-white" data-testid="launch-code-copy-icon"><Copy className="w-4 h-4" /></button>
          </div>
          {alreadyClaimed ? (
            <>
              <p className="text-emerald-400 font-medium">You already claimed the bonus.</p>
              <p className="text-sm text-slate-400 mt-1">Head to your dashboard to see the credits.</p>
              <Link to="/dashboard"><Button className="mt-5 bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold w-full">Go to dashboard <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </>
          ) : soldOut ? (
            <>
              <p className="text-rose-400 font-medium">Launch promo sold out.</p>
              <p className="text-sm text-slate-400 mt-1">Fastest 100 people got their bonus. Regular free tier still gives 500 credits.</p>
              <Link to="/register"><Button className="mt-5 w-full">Sign up for the free tier</Button></Link>
            </>
          ) : (
            <>
              <Button data-testid="btn-launch-claim" onClick={claim} disabled={claiming} className="w-full bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold h-11">
                {claiming ? "Claiming…" : user ? "Claim my 500 bonus credits" : "Sign up & claim"}
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                {user ? "Instant credit — no card required." : <>Already have an account? <Link to="/login?ref=launch" className="text-cyan-signal hover:underline">Sign in →</Link></>}
              </p>
            </>
          )}
        </div>

        <div className="mt-14 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {[
            { icon: Zap,      title: "Ships live in 60 sec", desc: "Register, generate a key, hit the API." },
            { icon: Rocket,   title: "MCP-native",           desc: "Plug into Claude, ChatGPT and LangGraph." },
            { icon: Sparkles, title: "Credits never expire in 3 months", desc: "Rollover baked in — unused credits stick around." },
          ].map((f) => (
            <div key={f.title} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <f.icon className="w-4 h-4 text-cyan-signal mb-2" />
              <p className="font-medium text-sm">{f.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
